import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

import type { MessageListItem } from '@/library/message/types';
import { InteractionView } from '@/stores/useChatWidget';
import type { RegistryId } from '@pleased-npm/fe-customisation';
import type {
  ActiveOpenChat,
  VerifyChatOpenDto,
  ArticleDetail,
  ActiveComments,
  ListTicketDetail,
  EntityReview,
  SearchArticle,
  FeedbackCategory,
} from '@/common/api/models.pleased';
import type { MockedSearchState, MockedConnectData } from './mocks';

export interface MockedFlowState {
  /** Whether mocked flow is active */
  isActive: boolean;
  /** The registry ID being mocked */
  registryId: RegistryId | null;
  /** Messages to inject into the conversation */
  messages: MessageListItem[];
  /** Whether to show the chat overlay */
  showChatOverlay: boolean;
  /** Interaction view to set */
  interactionView: InteractionView | null;
  /** Route to navigate to */
  navigateTo: string | null;
  /** Whether to skip the default chatbot flow */
  skipDefaultFlow: boolean;
  /** Whether to scroll to the last message after injection */
  scrollToBottom: boolean;
  /** Mocked resumable chat data */
  resumableChat: ActiveOpenChat | null;
  /** Mocked chat verification data */
  verifyChatOpen: VerifyChatOpenDto | null;
  /** Mocked search data (query + results) */
  searchState: MockedSearchState | null;
  /** Mocked article data */
  mockedArticle: ArticleDetail | null;
  /** Mocked related articles data */
  relatedArticles: SearchArticle[] | null;
  /** Mocked conversation data (comments and ticket) */
  mockedConversation: ActiveComments | null;
  /** Mocked recent resolved ticket data */
  recentResolvedTicket: { ticket: ListTicketDetail; reviews: EntityReview[]; ticketReviews: EntityReview[] } | null;
  /** Mocked open conversations list */
  openConversations: { data: ListTicketDetail[]; total: number } | null;
  /** Mocked closed/resolved conversations list */
  closedConversations: { data: ListTicketDetail[]; total: number } | null;
  /** Mocked feedback selections */
  feedbackSelections: FeedbackCategory[] | null;
  /** Mocked selected improvements (pre-selected feedback IDs) */
  selectedImprovements: string[] | null;
  /** Mocked connect data (queue position, agents online, wait times) */
  connectData: MockedConnectData | null;
}

const initialState: MockedFlowState = {
  isActive: false,
  registryId: null,
  messages: [],
  showChatOverlay: false,
  interactionView: null,
  navigateTo: null,
  skipDefaultFlow: false,
  scrollToBottom: true,
  resumableChat: null,
  verifyChatOpen: null,
  searchState: null,
  mockedArticle: null,
  relatedArticles: null,
  mockedConversation: null,
  recentResolvedTicket: null,
  openConversations: null,
  closedConversations: null,
  feedbackSelections: null,
  selectedImprovements: null,
  connectData: null,
};

/**
 * Store for managing mocked chatbot flow state.
 * This store holds the state for mocked flows including messages to inject into the conversation.
 * It's populated in App.vue initProject and consumed by useChatbotFlow.
 */
export const useMockedFlowStore = defineStore('mockedFlow', () => {
  const state = ref<MockedFlowState>({ ...initialState });

  const isActive = computed(() => state.value.isActive);
  const messages = computed(() => state.value.messages);
  const hasMessages = computed(() => state.value.messages.length > 0);
  const shouldSkipDefaultFlow = computed(() => state.value.skipDefaultFlow);
  const scrollToBottom = computed(() => state.value.scrollToBottom);
  const resumableChat = computed(() => state.value.resumableChat);
  const verifyChatOpen = computed(() => state.value.verifyChatOpen);
  const hasResumableChat = computed(() => state.value.resumableChat !== null);
  const searchState = computed(() => state.value.searchState);
  const hasSearchState = computed(() => state.value.searchState !== null);
  const mockedArticle = computed(() => state.value.mockedArticle);
  const hasMockedArticle = computed(() => state.value.mockedArticle !== null);
  const relatedArticles = computed(() => state.value.relatedArticles);
  const hasRelatedArticles = computed(() => state.value.relatedArticles !== null);
  const mockedConversation = computed(() => state.value.mockedConversation);
  const hasMockedConversation = computed(() => state.value.mockedConversation !== null);
  const recentResolvedTicket = computed(() => state.value.recentResolvedTicket);
  const hasRecentResolvedTicket = computed(() => state.value.recentResolvedTicket !== null);
  const openConversations = computed(() => state.value.openConversations);
  const hasOpenConversations = computed(() => state.value.openConversations !== null);
  const closedConversations = computed(() => state.value.closedConversations);
  const hasClosedConversations = computed(() => state.value.closedConversations !== null);
  const feedbackSelections = computed(() => state.value.feedbackSelections);
  const hasFeedbackSelections = computed(() => state.value.feedbackSelections !== null);
  const selectedImprovements = computed(() => state.value.selectedImprovements);
  const hasSelectedImprovements = computed(() => state.value.selectedImprovements !== null);
  const connectData = computed(() => state.value.connectData);
  const hasConnectData = computed(() => state.value.connectData !== null);

  /**
   * Set the mocked flow state
   */
  const setMockedFlow = (newState: Partial<MockedFlowState>) => {
    state.value = {
      ...state.value,
      ...newState,
      isActive: true,
    };
  };

  /**
   * Add messages to the mocked flow
   */
  const addMessages = (...newMessages: MessageListItem[]) => {
    state.value.messages = [...state.value.messages, ...newMessages];
  };

  /**
   * Clear the mocked flow state after messages have been consumed
   */
  const clearMessages = () => {
    state.value.messages = [];
  };

  /**
   * Reset the entire mocked flow state
   */
  const reset = () => {
    state.value = { ...initialState };
  };

  /**
   * Mark mocked flow as consumed (keep isActive for overlay blocking, but clear transient state)
   */
  const markConsumed = () => {
    state.value.messages = [];
    state.value.navigateTo = null;
  };

  return {
    state,
    isActive,
    messages,
    hasMessages,
    shouldSkipDefaultFlow,
    scrollToBottom,
    resumableChat,
    verifyChatOpen,
    hasResumableChat,
    searchState,
    hasSearchState,
    mockedArticle,
    hasMockedArticle,
    relatedArticles,
    hasRelatedArticles,
    mockedConversation,
    hasMockedConversation,
    recentResolvedTicket,
    hasRecentResolvedTicket,
    openConversations,
    hasOpenConversations,
    closedConversations,
    hasClosedConversations,
    feedbackSelections,
    hasFeedbackSelections,
    selectedImprovements,
    hasSelectedImprovements,
    connectData,
    hasConnectData,
    setMockedFlow,
    addMessages,
    clearMessages,
    reset,
    markConsumed,
  };
});
